import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useNotification } from '../../context/NotificationContext'
import { useAuth } from '../../context/AuthContext'
import client from '../../api/client'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'

function VerifyEmail() {
 const { token } = useParams()
 const navigate = useNavigate()
 const { add } = useNotification()
 const { login } = useAuth()
 const [status, setStatus] = useState('verifying') // 'verifying' | 'success' | 'error'
 const [message, setMessage] = useState('Verifying your email...')

 const verificationStarted = useRef(false)

 useEffect(() => {
 if (verificationStarted.current) return
 verificationStarted.current = true

 const verify = async () => {
 try {
 const { data } = await client.get(`/auth/verify-email?token=${token}`)
 
 setStatus('success')
 setMessage(data.message || 'Email verified successfully!')
 add('Email verified! Logging you in...', 'success')
 
 // Auto-login
 if (data.token && data.user) {
 login(data.token, data.user)
 
 setTimeout(() => {
 navigate('/', { replace: true })
 }, 2000)
 } else {
 setTimeout(() => {
 navigate('/login', { replace: true })
 }, 2000)
 }
 } catch (err) {
 setStatus('error')
 setMessage(err.response?.data?.message || 'Verification failed. The link may be invalid or expired.')
 add(err.response?.data?.message || 'Verification failed', 'error')
 }
 }
 verify()
 }, [token, navigate, add, login])

 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <Card className="w-full max-w-md shadow-lg">
 <CardContent className="py-12 text-center">
 {status === 'verifying' && (
 <>
 <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
 <h2 className="text-2xl font-bold mb-2">Verifying Email</h2>
 <p className="text-muted-foreground">{message}</p>
 </>
 )}

 {status === 'success' && (
 <>
 <div className="w-12 h-12 rounded-full bg-green-500/100/10 flex items-center justify-center mx-auto mb-4">
 <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
 </div>
 <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
 <p className="text-muted-foreground mb-6">{message}</p>
 <p className="text-sm text-muted-foreground">Redirecting you to the homepage...</p>
 </>
 )}

 {status === 'error' && (
 <>
 <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
 <XCircle className="w-6 h-6 text-destructive" />
 </div>
 <h2 className="text-2xl font-bold mb-2">Verification Failed</h2>
 <p className="text-muted-foreground mb-6">{message}</p>
 <div className="space-y-2">
 <Button asChild className="w-full">
 <Link to="/login">Go to Login</Link>
 </Button>
 <Button asChild variant="outline" className="w-full">
 <Link to="/">Go to Homepage</Link>
 </Button>
 </div>
 </>
 )}
 </CardContent>
 </Card>
 </div>
 )
}

export default VerifyEmail
